import { createContext, useLayoutEffect, useMemo } from "react";
import { createTheme, type Theme } from "@mui/material/styles";
import { useSelector, useDispatch } from "react-redux";
import { IColorShades } from "./store/slice/ThemeSlice";
import { IColorTokens } from "./types/theme";
import { RootState, AppDispatch } from "./store/Index";
import { toggleMode } from "./store/slice/ThemeSlice";

// WCAG relative luminance (0 = black, 1 = white). Used to decide whether an
// admin-configured palette already suits dark mode or needs an override.
const luminance = (hex: string): number => {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex?.trim?.() || "");
  if (!m) return 0.5;
  let s = m[1];
  if (s.length === 3) s = s[0] + s[0] + s[1] + s[1] + s[2] + s[2];
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(s.substr(i, 2), 16) / 255);
  const lin = (c: number) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};

// Keep admin's color if it already suits dark mode; otherwise use the fallback.
// `wantDark` = true for bg/surface roles (want low luminance in dark mode),
// false for text/foreground roles (want high luminance in dark mode).
const pickForDark = (adminColor: string, fallback: string, wantDark: boolean): string => {
  if (!adminColor) return fallback;
  const lum = luminance(adminColor);
  if (wantDark) return lum < 0.25 ? adminColor : fallback;
  return lum > 0.6 ? adminColor : fallback;
};

// Swap light/dark semantic colors for dark mode so every component adapts automatically.
// Admin-configured brand accents (10/50/200/400/500/600) are ALWAYS preserved.
// Role-locked shades (bg/text/surface) keep the admin value IF it already suits
// dark mode (e.g. admin shipped a pre-inverted palette), otherwise use the
// hardcoded dark fallback for readability.
const invertForDark = (light: IColorShades): IColorShades => ({
  10: light[10],
  50: light[50],
  100: pickForDark(light[100], "#E2E8F0", false),  // secondary text
  200: light[200],
  300: pickForDark(light[300], "#CBD5E1", false),  // muted text
  400: light[400],                                  // brand accent — always admin
  500: light[500],                                  // brand highlight — always admin
  600: light[600],                                  // brand pale — always admin
  700: pickForDark(light[700], "#F8FAFC", false),  // primary text
  800: pickForDark(light[800], "#0B1120", true),   // page background
  900: pickForDark(light[900], "#111827", true),   // card/paper surface
});

const invertSecondaryForDark = (light: IColorShades): IColorShades => ({
  10: light[10],
  50: light[50],
  100: pickForDark(light[100], "#E2E8F0", false),
  200: pickForDark(light[200], "#1E293B", true),
  300: pickForDark(light[300], "#CBD5E1", false),
  400: light[400],
  500: light[500],
  600: pickForDark(light[600], "#1a2234", true),
  700: pickForDark(light[700], "#F8FAFC", false),
  800: pickForDark(light[800], "#0B1120", true),
  900: pickForDark(light[900], "#111827", true),
});

const invertNeutralForDark = (light: IColorShades): IColorShades => ({
  10: light[10],
  50: light[50],
  100: pickForDark(light[100], "#E2E8F0", false),
  200: pickForDark(light[200], "#1a2332", true),
  300: light[300],
  400: pickForDark(light[400], "#94A3B8", false),
  500: pickForDark(light[500], "#CBD5E1", false),
  600: light[600],
  700: pickForDark(light[700], "#1E293B", true),
  800: pickForDark(light[800], "#F1F5F9", false),
  900: pickForDark(light[900], "#0F172A", true),
});

// Function to return color tokens based on mode and Redux theme state
export const tokens = (
  mode: "light" | "dark",
  themeState?: RootState["theme"]
): IColorTokens => {
  // Get the base light-mode colors (from Redux or hardcoded fallback)
  const lightColors: IColorTokens = themeState
    ? {
        primary: themeState.primary_colors,
        secondary: themeState.secondary_colors,
        neutral: themeState.neutral_colors,
        grey: themeState.grey_colors || themeState.neutral_colors,
      }
    : {
        primary: {
          10: "#C29363",
          50: "#C29363",
          100: "#111827",
          200: "#D1A07740",
          300: "#94A3B8",
          400: "#724E41",
          500: "#D1A077",
          600: "#E5BFA5",
          700: "#000000",
          800: "#f0f4f8",
          900: "#ffffff",
        },
        secondary: {
          10: "#6870fa",
          50: "#6870fa",
          100: "#868dfb",
          200: "#333",
          300: "#666",
          400: "#503629",
          500: "#F7E6D8",
          600: "#F1F5F9",
          700: "#E5BFA5",
          800: "#C9B1A1",
          900: "#E5BFA5",
        },
        neutral: {
          10: "#ff0000",
          50: "#ff0000",
          100: "#4CAF50",
          200: "#F0FDFA",
          300: "#0D9488",
          400: "#64748B",
          500: "#475569",
          600: "#334155",
          700: "#F3F4F6",
          800: "#0F172A",
          900: "#F1F5F9",
        },
        grey: {
          10: "#25d366",
          50: "#4267B2",
          100: "#365899",
          200: "#128C7E",
          300: "#FF0000",
          400: "#CC0000",
          500: "#e0e0e0",
          600: "#000000",
          700: "#000000",
          800: "#000000",
          900: "#000000",
        },
      };

  if (mode === "light") {
    return lightColors;
  }

  // Dark mode: invert semantic colors so all components adapt automatically
  return {
    primary: invertForDark(lightColors.primary),
    secondary: invertSecondaryForDark(lightColors.secondary),
    neutral: invertNeutralForDark(lightColors.neutral),
    grey: lightColors.grey, // grey is social media / special colors — keep as-is
  };
};

// Define the theme settings
export const themeSettings = (
  mode: "light" | "dark",
  themeState?: RootState["theme"]
) => {
  const colors = tokens(mode, themeState);

  return {
    palette: {
      mode: mode,
      primary: {
        main: mode === "dark" ? colors.primary[500] : colors.primary[400],
      },
      secondary: {
        main: colors.secondary[50],
      },
      neutral: {
        main: colors.neutral[500],
      },
      background: {
        default: colors.primary[800],
        paper: colors.primary[900],
      },
      text: {
        primary: colors.primary[700],
        secondary: colors.primary[300],
      },
      ...(mode === "dark" && {
        divider: colors.primary[300] + "25",
        action: {
          hover: colors.primary[300] + "12",
          selected: colors.primary[400] + "20",
          disabled: colors.primary[300] + "60",
          disabledBackground: colors.primary[300] + "18",
        },
      }),
    },
    typography: {
      fontFamily: '"Mulish", sans-serif',
      fontSize: 12,
      h1: { fontSize: 40 },
      h2: { fontSize: 32 },
      h3: { fontSize: 24 },
      h4: { fontSize: 20 },
      h5: { fontSize: 16 },
      h6: { fontSize: 14 },
    },
    components: mode === "dark" ? {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: colors.primary[700],
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.primary[300] + "30",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.primary[400],
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.primary[500],
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: colors.primary[300],
            "&.Mui-focused": {
              color: colors.primary[500],
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            "&::placeholder": {
              color: colors.primary[300],
              opacity: 0.7,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            color: colors.primary[700],
            borderBottomColor: colors.primary[300] + "20",
          },
          head: {
            color: colors.primary[300],
            fontWeight: 600,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: colors.primary[800],
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: colors.primary[300] + "20",
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: colors.primary[300],
            "&.Mui-selected": {
              color: colors.primary[500],
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            color: colors.primary[700],
            borderColor: colors.primary[300] + "30",
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: {
            color: colors.primary[300],
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: colors.primary[700],
            "&:hover": {
              backgroundColor: colors.primary[300] + "12",
            },
            "&.Mui-selected": {
              backgroundColor: colors.primary[400] + "20",
              "&:hover": {
                backgroundColor: colors.primary[400] + "30",
              },
            },
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            color: colors.primary[700],
          },
          secondary: {
            color: colors.primary[300],
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: colors.primary[300],
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: colors.primary[700],
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            color: "inherit",
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            color: colors.primary[700],
          },
        },
      },
    } : {},
  };
};

interface ColorModeContextType {
  toggleColorMode: () => void;
  mode: "light" | "dark";
}

// Context with default value
export const ColorModeContext = createContext<ColorModeContextType>({
  toggleColorMode: () => {},
  mode: "light",
});

// Custom hook to manage theme mode with per-user Redux-persisted state
export const useMode = (): [Theme, ColorModeContextType] => {
  const dispatch = useDispatch<AppDispatch>();
  const themeState = useSelector((state: RootState) => state.theme);
  const username = useSelector((state: RootState) => state.auth.user?.username);
  const mode = themeState.mode;

  const colorMode = useMemo<ColorModeContextType>(
    () => ({
      toggleColorMode: () => {
        dispatch(toggleMode(username));
      },
      mode,
    }),
    [mode, dispatch, username]
  );

  const theme = useMemo<Theme>(
    () => createTheme(themeSettings(mode, themeState)),
    [mode, themeState]
  );

  // Bridge MUI mode → document root so plain CSS / CSS variables can react.
  // useLayoutEffect to avoid a flash of the wrong theme on initial paint.
  useLayoutEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.dataset.theme = mode;
    }
  }, [mode]);

  return [theme, colorMode];
};

export type ThemeColors = ReturnType<typeof tokens>;

/** Calendar UI roles mapped from theme tokens (light/dark + admin palette). */
export type CalendarPalette = {
  border: string;
  gridLine: string;
  surface: string;
  pageBg: string;
  columnHeader: string;
  todayColumn: string;
  todayColumnFill: string;
  textPrimary: string;
  textSecondary: string;
  accent: string;
  eventBg: string;
  liveBorder: string;
  liveBg: string;
  inactiveBg: string;
  inactiveBorder: string;
  tooltipShadow: string;
  slotShadow: string;
  slotShadowHover: string;
};

/** Normalize #rgb / #rrggbb / #rrggbbaa to #rrggbb + separate alpha suffix. */
const withAlpha = (color: string, alphaHex: string): string => {
  const raw = color?.trim() || "";
  if (!raw) return raw;
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(raw);
  if (!m) return raw;
  let s = m[1];
  if (s.length === 3) {
    s = s[0] + s[0] + s[1] + s[1] + s[2] + s[2];
  }
  const rgb = s.slice(0, 6);
  return `#${rgb}${alphaHex}`;
};

/** Availability weekly calendar colors derived from `tokens()`. */
export const getCalendarPalette = (
  mode: "light" | "dark",
  colorTokens: IColorTokens,
): CalendarPalette => {
  const { primary, secondary, neutral } = colorTokens;
  const isDark = mode === "dark";

  const border = withAlpha(primary[300], isDark ? "40" : "55");
  const gridLine = withAlpha(primary[300], isDark ? "55" : "99");
  const todayTint = secondary[600] || primary[200];

  return {
    border,
    gridLine,
    surface: primary[900],
    pageBg: primary[800],
    columnHeader: primary[800],
    todayColumn: withAlpha(todayTint, isDark ? "99" : "dd"),
    todayColumnFill: withAlpha(todayTint, isDark ? "50" : "6b"),
    textPrimary: primary[700],
    textSecondary: primary[300],
    accent: primary[400],
    eventBg: withAlpha(primary[600], isDark ? "99" : "cc"),
    liveBorder: neutral[100],
    liveBg: withAlpha(neutral[100], isDark ? "55" : "99"),
    inactiveBg: withAlpha(primary[300], isDark ? "45" : "35"),
    inactiveBorder: primary[300],
    tooltipShadow: isDark
      ? `0 4px 12px ${withAlpha(primary[800], "e6")}`
      : `0 4px 12px ${withAlpha(primary[700], "33")}`,
    slotShadow: isDark
      ? `0 1px 3px ${withAlpha(primary[800], "b3")}`
      : `0 1px 3px ${withAlpha(primary[700], "26")}`,
    slotShadowHover: isDark
      ? `0 3px 8px ${withAlpha(primary[800], "e6")}`
      : `0 3px 8px ${withAlpha(primary[700], "40")}`,
  };
};
